import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { US_COUNTRY_CODE } from '@glass/common/modules/geo/data/countries';
import usStates from '@glass/common/modules/geo/usStates';
import parseDisplayName from '@glass/common/modules/resumes/parseDisplayName';
import removeEmptyKeys from '@glass/common/modules/utils/removeEmptyKeys';
import {
  CITY_FIELD_V2,
  CONTACT_FIELD_V2,
  COUNTRY_FIELD_V2,
  EMAIL_FIELD_V2,
  FIRST_NAME_FIELD_V2,
  LAST_NAME_FIELD_V2,
  LOCATION_FIELD_V2,
  PHONE_FIELD_V2,
  RESUME_V2_FIELD,
  STATE_FIELD_V2,
  STREET_FIELD_V2,
  ZIP_CODE_FIELD_V2,
} from '@glass/web/modules/resumes/fieldKeys';

import selectUserResumeCity from '@glass/shared/modules/userConfig/selectors/selectUserResumeCity';
import selectUserResumeContactEmail from '@glass/shared/modules/userConfig/selectors/selectUserResumeContactEmail';
import selectUserResumeCountryCode from '@glass/shared/modules/userConfig/selectors/selectUserResumeCountryCode';
import selectUserResumeDisplayName from '@glass/shared/modules/userConfig/selectors/selectUserResumeDisplayName';
import selectUserResumePhone from '@glass/shared/modules/userConfig/selectors/selectUserResumePhone';
import selectUserResumePostalCode from '@glass/shared/modules/userConfig/selectors/selectUserResumePostalCode';
import selectUserResumeStateCode from '@glass/shared/modules/userConfig/selectors/selectUserResumeStateCode';
import selectUserResumeStreet from '@glass/shared/modules/userConfig/selectors/selectUserResumeStreet';

const createResumeInitialValues = ({
  firstName,
  lastName,
  email,
  phone,
  street,
  city,
  state,
  zip,
  country,
}) => {
  return {
    [RESUME_V2_FIELD]: {
      [CONTACT_FIELD_V2]: removeEmptyKeys({
        [FIRST_NAME_FIELD_V2]: firstName,
        [LAST_NAME_FIELD_V2]: lastName,
        [EMAIL_FIELD_V2]: email,
        [PHONE_FIELD_V2]: phone,
        [LOCATION_FIELD_V2]: removeEmptyKeys({
          [STREET_FIELD_V2]: street,
          [CITY_FIELD_V2]: city,
          [STATE_FIELD_V2]: state,
          [ZIP_CODE_FIELD_V2]: zip,
          [COUNTRY_FIELD_V2]: country || US_COUNTRY_CODE,
        }),
      }),
    },
  };
};

// priority: profile, payment form, geo
const useContactInitialValues = ({ isMinimal } = {}) => {
  const city = useSelector(selectUserResumeCity);
  const stateCode = useSelector(selectUserResumeStateCode);
  const zip = useSelector(selectUserResumePostalCode);
  const countryCode = useSelector(selectUserResumeCountryCode);
  const displayName = useSelector(selectUserResumeDisplayName);
  const street = useSelector(selectUserResumeStreet);
  const email = useSelector(selectUserResumeContactEmail);
  const phone = useSelector(selectUserResumePhone);

  return useMemo(() => {
    const { firstName, lastName } = parseDisplayName(displayName);
    return createResumeInitialValues({
      firstName,
      lastName,
      email,
      phone: isMinimal ? undefined : phone,
      street: isMinimal ? undefined : street,
      city,
      state: stateCode && usStates.find(({ code }) => code === stateCode)?.label,
      zip: isMinimal ? undefined : zip,
      country: countryCode,
    });
  }, [displayName, email, isMinimal, phone, street, city, stateCode, zip, countryCode]);
};

export default useContactInitialValues;
